// src/components/organisms/PatientWorkspaceTabs/TreatmentEditor.js
//
// Tedavi kayıt editörü. Master-detail layout'unun sağ paneli.
// Tüm autosave mantığı `useInlineAutosave` hook'undadır.
//
// Mode-bazlı davranış:
// - "standalone" (Hasta Detay): manualCreate=true; "Kaydet" butonu görünür,
//   lazy create yok.
// - "session" (Muayene): manualCreate=false; ilk tanı eklendiğinde POST.
//
// Footer aksiyonları (autosave akışı dışı, eksplisit):
// - Sil → onDelete(record) → caller handles confirm + delete
// - Aktif Yap → setActiveTreatment (yalnız !record.isActive iken görünür)
// - Durum ▾ → setTreatmentStatus (FormSelect)

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Skeleton,
  MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Delete as DeleteIcon,
  LocalPharmacy as LocalPharmacyIcon,
  ReportProblem as ReportProblemIcon,
  Restaurant as RestaurantIcon,
  KeyboardArrowDownRounded as ArrowDownIcon,
  MedicalServices as MedicalServicesIcon,
} from "@mui/icons-material";

import FormSelect from "../../atoms/FormSelect";
import FormTextField from "../../atoms/FormTextField";
import FormDatePicker from "../../atoms/FormDatePicker";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import ActionButton from "../../atoms/ActionButton";
import useInlineAutosave from "../../../hooks/useInlineAutosave";
import {
  useListDiagnosesMutation,
  useListComplaintsMutation,
  useListMedicineTemplatesMutation,
  useListFoodsMutation,
} from "../../../store/systemVariablesApi";
import TagPickerDrawer from "./drawers/TagPickerDrawer";
import {
  SECTION_SX,
  SECTION_HEADING_SX,
  GROUP_BOX_SX,
  GROUP_TITLE_SX,
  listAreaSx,
  ITEM_ROW_SX,
  ITEM_NAME_SX,
  codeBadgeSx,
  FieldRow,
  SectionHeader,
  SectionEmpty,
  EditorHeader,
  PdfButton,
} from "./_editorParts";
import MedicationPickerDrawer from "./drawers/MedicationPickerDrawer";
import { useClinicModule } from "../../../hooks/useClinicModule";
import {
  useCreateTreatmentMutation,
  useUpdateTreatmentMutation,
  useSetActiveTreatmentMutation,
  useSetTreatmentStatusMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

// @react-pdf/renderer + gömülü fontlar ~430 kB gzip. Tembel yüklenir: PDF
// çıktısı alınmayan her sayfa açılışına bu maliyeti bindirmenin anlamı yok.
// Seçim drawer'larında sunucudan sayfa başına gelen kayıt
const PICKER_PAGE_SIZE = 30;

// Sistem değişkeni listesinden sayfalı arama fonksiyonu üretir
const makeFetchPage = (trigger, token, pickItems, toOption) => async ({ query, page }) => {
  const res = await trigger({
    token,
    status: "active",
    query: query || undefined,
    page,
    limit: PICKER_PAGE_SIZE,
  }).unwrap();
  const items = pickItems(res?.data || {}).map(toOption).filter((o) => o && o.value);
  return { items, hasMore: page < (res?.data?.pagination?.pages || 0) };
};

const TreatmentPlanPreview = lazy(() => import("../pdf/TreatmentPlanPreview"));

// ── Helpers ──

const getRefId = (value) =>
  value && typeof value === "object" ? value._id : value;

const mapFoodsFromTreatment = (list) =>
  (list || [])
    .map((it) => {
      const id =
        it.food && typeof it.food === "object" ? it.food._id : it.food;
      const name = it.name || (it.food && it.food.name) || "";
      if (!id) return null;
      return {
        _id: id,
        food: id,
        name,
        amount: it.amount != null ? String(it.amount) : "",
        amountUnit: it.amountUnit != null ? String(it.amountUnit) : "",
        notes: it.notes != null ? String(it.notes) : "",
      };
    })
    .filter(Boolean);

const EMPTY_MEDICINE_FORM = {
  medicine: null,
  dose1: "",
  dose2: "",
  usagePeriodValue: "",
  usagePeriodUnit: "Gün",
  box: "",
  quantity: "",
  usageMethod: "",
};

const STATUS_OPTIONS = [
  { value: "active", label: "Aktif" },
  { value: "completed", label: "Tamamlandı" },
  { value: "abandoned", label: "Bırakıldı" },
  { value: "cancelled", label: "İptal Edildi" },
];

// Durum renkleri (modern seçici için renkli nokta + metin).
const statusColor = (value, palette) => {
  switch (value) {
    case "completed":
      return COLORS.successDark;
    case "abandoned":
      return COLORS.warningDark;
    case "cancelled":
      return COLORS.error;
    case "active":
    default:
      return palette?.primary || COLORS.slate700;
  }
};

// useInlineAutosave -> fieldsFromRecord
const treatmentFieldsFromRecord = (record) => {
  if (!record) {
    return {
      diagnoses: [],
      complaints: [],
      specialNotes: "",
      medications: [],
      allowedFoods: [],
      forbiddenFoods: [],
      nextControlDate: "",
      nextControlNote: "",
      prescriptionNote: "",
      dietNote: "",
      prescriptionRequested: false,
      dietRequested: false,
    };
  }
  const rawAllowed = record.allowedFoods || record.dietInfo?.allowedList || [];
  const rawForbidden =
    record.forbiddenFoods || record.dietInfo?.forbiddenList || [];
  return {
    diagnoses: record.diagnoses || [],
    complaints: record.complaints || [],
    specialNotes: record.specialNotes || "",
    medications: record.medications || [],
    allowedFoods: mapFoodsFromTreatment(rawAllowed),
    forbiddenFoods: mapFoodsFromTreatment(rawForbidden),
    nextControlDate: record.nextControlDate
      ? String(record.nextControlDate).slice(0, 10)
      : "",
    nextControlNote: record.nextControlNote || "",
    prescriptionNote: record.prescriptionNote || "",
    dietNote: record.dietNote || "",
    prescriptionRequested: Boolean(record.prescriptionRequested),
    dietRequested: Boolean(record.dietRequested),
  };
};

// Payload builder
const buildTreatmentSavePayload = (form) => ({
  diagnoses: (form.diagnoses || [])
    .map((d) => ({
      diagnosisCategory: getRefId(d.diagnosisCategory),
      diagnosisCategoryName:
        d.diagnosisCategoryName ||
        (d.diagnosisCategory &&
          (d.diagnosisCategory.name || d.diagnosisCategory.categoryName)),
      diagnosisDisease: getRefId(d.diagnosisDisease),
      diagnosisName:
        d.diagnosisName || (d.diagnosisDisease && d.diagnosisDisease.name),
      diagnosisCode: d.diagnosisCode || "",
    }))
    .filter((d) => d.diagnosisCategory && d.diagnosisDisease),
  complaints: (form.complaints || []).filter(
    (c) => typeof c === "string" && c.trim()
  ),
  // Notlar boşaltılınca "" gönderilir ki sunucuda da silinsin
  specialNotes: form.specialNotes ?? "",
  medications: (form.medications || []).map((m) => ({
    ...m,
    medicine: getRefId(m.medicine),
    isTaken: Boolean(m.isTaken),
  })),
  prescriptionNote: form.prescriptionNote ?? "",
  dietNote: form.dietNote ?? "",
  prescriptionRequested:
    (form.medications || []).length > 0
      ? false
      : Boolean(form.prescriptionRequested),
  dietRequested: Boolean(form.dietRequested),
  // allowedFoods/forbiddenFoods gönderilmez: diyet listeleri DietPlan'da
  // (sunucu, alan gelmezse Treatment.dietInfo'ya dokunmaz).
  nextControlDate: form.nextControlDate || undefined,
  nextControlNote: form.nextControlNote || undefined,
});


// Reçete / diyet / şikayet / tanı listeleri sabit yükseklikte (bkz. _editorParts)
const MED_LIST_HEIGHT = 264;
const COMPLAINT_LIST_HEIGHT = 216;
const DIAGNOSIS_LIST_HEIGHT = 168;

// ── Component ──

export default function TreatmentEditor({
  record,
  patient,
  patientId,
  token,
  examinationId,
  canEdit = true,
  onCreated,
  onSaved,
  onSetActive,
  onSetStatus,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onError,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const prescriptionModule = useClinicModule("prescription");
  const dietModule = useClinicModule("diet");

  const [createTreatment] = useCreateTreatmentMutation();
  const [updateTreatment] = useUpdateTreatmentMutation();
  const [setActiveTreatment] = useSetActiveTreatmentMutation();
  const [setTreatmentStatus] = useSetTreatmentStatusMutation();

  // Tüm modlarda otomatik kayıt: yeni tedavi (tanı+şikayet dolunca) ve seçili
  // tedavi düzenlemesi debounce ile otomatik kaydedilir. Manuel "Kaydet" yok.
  const useManualCreate = false;

  const createFn = useCallback(
    async (payload) => {
      const res = await createTreatment(payload).unwrap();
      return res?.data?.treatment || res?.treatment || res;
    },
    [createTreatment]
  );
  const updateFn = useCallback(
    async (payload) => {
      const res = await updateTreatment(payload).unwrap();
      return res?.data?.treatment || res?.treatment || res;
    },
    [updateTreatment]
  );

  const { fieldValues, setField, saveStatus } = useInlineAutosave({
    record,
    createMutation: createFn,
    updateMutation: updateFn,
    fieldsFromRecord: treatmentFieldsFromRecord,
    // Tanı + en az 1 şikayet zorunlu (sunucu da bunu şart koşuyor). İkisi de
    // dolmadan kayıt denenmez — böylece yarım veride 400 alınmaz.
    shouldCreate: (form) =>
      (form.diagnoses?.length || 0) > 0 && (form.complaints?.length || 0) > 0,
    canSave: (form) =>
      (form.diagnoses?.length || 0) > 0 && (form.complaints?.length || 0) > 0,
    createTriggerField: useManualCreate ? null : "diagnoses",
    createDebounceMs: 200,
    debounceMs: 500,
    manualCreate: useManualCreate,
    // Güncelleme başarısızsa (402 token yetersiz, 423 vb.) formu sunucudaki son
    // hale geri al — başarısız değişiklik UI'da kalmasın.
    revertOnError: true,
    buildPayload: (form) => ({
      token,
      patientId,
      ...(examinationId ? { examinationId } : {}),
      ...buildTreatmentSavePayload(form),
    }),
    recordIdField: "treatmentId",
    onCreated,
    onSaved,
    onError,
    registerAutoSave,
    registerFlush,
  });

  useEffect(() => {
    if (typeof onSaveStatusChange === "function") onSaveStatusChange(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  const isDisabled = !canEdit;
  const isNew = !record?._id;

  // Durum değişimi (Aktif Yap / durum select) sürerken: form skeleton'a döner,
  // durum kontrolü kilitlenir, seçilen yeni durum sonuç gelene kadar optimistic
  // gösterilir.
  const [statusChanging, setStatusChanging] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  // Tanı zorunlu alandır. En az bir tanı eklenene kadar diğer tüm alanlar
  // (şikayet, ilaç, besin, notlar, kontrol) kilitli kalır.
  const hasDiagnosis = (fieldValues.diagnoses?.length || 0) > 0;
  const lockedNoDx = isDisabled || !hasDiagnosis;

  // İlaç ekleme formu (MedicationPickerDrawer bunu gösterir)
  const [medicineForm, setMedicineForm] = useState(EMPTY_MEDICINE_FORM);

  // Seçim drawer'ı: "complaint" | "medication" | "allowed" | "forbidden".
  // Anahtar kapanırken korunur ki kapanış animasyonunda içerik boşalmasın.
  const [picker, setPicker] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const openPicker = (key) => {
    if (key === "medication") setMedicineForm(EMPTY_MEDICINE_FORM);
    setPicker(key);
    setPickerOpen(true);
  };
  const closePicker = () => setPickerOpen(false);

  useEffect(() => {
    setMedicineForm(EMPTY_MEDICINE_FORM);
    setPickerOpen(false);
  }, [record?._id]);

  const [listDiagnoses] = useListDiagnosesMutation();
  const [listComplaints] = useListComplaintsMutation();
  const [listMedicineTemplates] = useListMedicineTemplatesMutation();
  const [listFoods] = useListFoodsMutation();

  const fetchDiagnoses = useMemo(
    () =>
      makeFetchPage(
        listDiagnoses,
        token,
        (d) => d.diagnoses || [],
        (o) => ({
          key: String(o._id),
          value: String(o._id),
          title: o.name,
          subtitle: o.code || undefined,
          raw: o,
        })
      ),
    [listDiagnoses, token]
  );
  const fetchComplaints = useMemo(
    () =>
      makeFetchPage(
      listComplaints,
      token,
      (d) => d.complaints || d.items || [],
      (c) => ({ key: c._id || c.name, value: c.name, title: c.name })
    ),
    [listComplaints, token]
  );
  const fetchMedicines = useMemo(
    () =>
      makeFetchPage(
      listMedicineTemplates,
      token,
      (d) => d.items || d.medicines || [],
      (m) => ({ key: String(m._id), value: m.name, title: m.name, raw: m })
    ),
    [listMedicineTemplates, token]
  );
  const fetchFoods = useMemo(
    () =>
      makeFetchPage(
      listFoods,
      token,
      (d) => d.foods || d.items || [],
      (f) => ({ key: String(f._id || f.id), value: f.name, title: f.name, raw: f })
    ),
    [listFoods, token]
  );

  // ── Chip / medication / food handlers ──

  // Tanı drawer'ı ham tanı seçeneğini ({_id, code, name, category}) ekler;
  // kayıt şekline ({diagnosisDisease, diagnosisName, …}) çevrilir. Mevcut
  // öğeler zaten kayıt şeklinde.
  const toDiagnosisEntry = (opt) => {
    if (!opt || opt.diagnosisDisease != null) return opt;
    const cat = opt.category;
    const catId = typeof cat === "object" ? cat?._id : cat || "";
    const catName = typeof cat === "object" ? cat?.name : "";
    return {
      diagnosisCategory: catId,
      diagnosisCategoryName: catName,
      diagnosisDisease: opt._id,
      diagnosisName: opt.name,
      diagnosisCode: opt.code || "",
    };
  };

  const handleDiagnosesChange = (next) => {
    setField("diagnoses", (next || []).map(toDiagnosisEntry).filter(Boolean));
  };

  // Drawer'dan gelen tanıları ekle (zaten olanları atla)
  const addDiagnoses = (options) => {
    const current = fieldValues.diagnoses || [];
    const seen = new Set(current.map((d) => String(getDiagnosisId(d))));
    const next = [...current];
    options.forEach((o) => {
      if (!o?.raw || seen.has(String(o.raw._id))) return;
      seen.add(String(o.raw._id));
      next.push(o.raw);
    });
    handleDiagnosesChange(next);
  };

  const removeDiagnosis = (idx) => {
    handleDiagnosesChange((fieldValues.diagnoses || []).filter((_, i) => i !== idx));
  };

  const getDiagnosisId = (o) =>
    o?.diagnosisDisease != null ? getRefId(o.diagnosisDisease) : o?._id;

  // Drawer'dan gelen şikayetleri tek seferde ekle (tekrarları atla)
  const addComplaints = (values) => {
    const current = fieldValues.complaints || [];
    const seen = new Set(current.map((c) => c.toLocaleLowerCase("tr-TR")));
    const next = [...current];
    values.forEach((v) => {
      const key = String(v || "").trim().toLocaleLowerCase("tr-TR");
      if (!key || seen.has(key)) return;
      seen.add(key);
      next.push(String(v).trim());
    });
    setField("complaints", next);
  };

  // İlaç seçilince form ilacın varsayılanlarıyla dolar
  const selectMedicine = (v) => {
    if (!v) {
      setMedicineForm(EMPTY_MEDICINE_FORM);
      return;
    }
    setMedicineForm({
      medicine: v,
      dose1: v?.dose1 != null ? String(v.dose1) : "",
      dose2: v?.dose2 != null ? String(v.dose2) : "",
      usagePeriodValue:
        v?.usagePeriod?.value != null ? String(v.usagePeriod.value) : "",
      usagePeriodUnit: v?.usagePeriod?.unit || "Gün",
      box: v?.box != null ? String(v.box) : "",
      quantity: v?.quantity != null ? String(v.quantity) : "",
      usageMethod: v?.usageMethod != null ? String(v.usageMethod) : "",
    });
  };

  const removeComplaint = (idx) => {
    setField(
      "complaints",
      (fieldValues.complaints || []).filter((_, i) => i !== idx)
    );
  };

  const addMedication = () => {
    const m = medicineForm.medicine;
    if (!m) return;
    const boxNum =
      medicineForm.box != null && String(medicineForm.box).trim() !== ""
        ? Number(medicineForm.box)
        : NaN;
    const qtyNum =
      medicineForm.quantity != null &&
      String(medicineForm.quantity).trim() !== ""
        ? Number(medicineForm.quantity)
        : NaN;
    if (!Number.isFinite(boxNum) || !Number.isFinite(qtyNum)) return;
    const exists = (fieldValues.medications || []).some(
      (x) => String(getRefId(x.medicine)) === String(m._id)
    );
    if (exists) return;
    setField("medications", [
      ...(fieldValues.medications || []),
      {
        medicine: m._id,
        name: m.name || "",
        dose1: medicineForm.dose1 ? Number(medicineForm.dose1) : undefined,
        dose2: medicineForm.dose2 ? Number(medicineForm.dose2) : undefined,
        usagePeriod: medicineForm.usagePeriodValue
          ? {
              value: Number(medicineForm.usagePeriodValue) || 0,
              unit: medicineForm.usagePeriodUnit || "Gün",
            }
          : undefined,
        box: boxNum,
        quantity: qtyNum,
        usageMethod: medicineForm.usageMethod || "",
        isTaken: false,
      },
    ]);
    setMedicineForm(EMPTY_MEDICINE_FORM);
    setPickerOpen(false);
  };

  const removeMedication = (idx) => {
    setField(
      "medications",
      (fieldValues.medications || []).filter((_, i) => i !== idx)
    );
  };

  const foodEntry = (food) => {
    const id = food?._id || food?.id;
    if (!id) return null;
    return {
      _id: id,
      food: id,
      name: food.name || "",
      amount:
        food.amount != null && food.amount !== "" ? String(food.amount) : "",
      amountUnit: food.amountUnit != null ? String(food.amountUnit) : "",
      notes: "",
    };
  };

  // Drawer'dan gelen besinleri tek seferde ekle (field: allowedFoods | forbiddenFoods)
  const addFoods = (field, foods) => {
    const current = fieldValues[field] || [];
    const seen = new Set(current.map((v) => String(v._id || v.food)));
    const next = [...current];
    foods.forEach((f) => {
      const entry = foodEntry(f);
      if (!entry || seen.has(String(entry._id))) return;
      seen.add(String(entry._id));
      next.push(entry);
    });
    setField(field, next);
  };

  const canAddMedication =
    medicineForm.medicine &&
    String(medicineForm.box ?? "").trim() &&
    String(medicineForm.quantity ?? "").trim();

  // ── Footer aksiyonları ──

  const handleSetActive = async () => {
    if (!record?._id || record.isActive || statusChanging) return;
    setStatusChanging(true);
    setPendingStatus("active");
    try {
      const promise = setActiveTreatment({
        token,
        patientId,
        treatmentId: record._id,
        ...(examinationId ? { examinationId } : {}),
      }).unwrap();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      await wrapped;
      if (typeof onSetActive === "function") onSetActive(record);
    } catch (err) {
      if (typeof onError === "function") onError(err);
    } finally {
      setStatusChanging(false);
      setPendingStatus(null);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    if (!record?._id || !nextStatus || nextStatus === record.status) return;
    if (statusChanging) return;
    setStatusChanging(true);
    setPendingStatus(nextStatus);
    try {
      const promise = setTreatmentStatus({
        token,
        patientId,
        treatmentId: record._id,
        status: nextStatus,
        ...(examinationId ? { examinationId } : {}),
      }).unwrap();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      await wrapped;
      if (typeof onSetStatus === "function") onSetStatus(record, nextStatus);
    } catch (err) {
      if (typeof onError === "function") onError(err);
    } finally {
      setStatusChanging(false);
      setPendingStatus(null);
    }
  };

  // Tek kontrol: "Aktif" seçilince tedaviyi aktif yapar (ayrı buton yok),
  // diğer durumlar status günceller.
  const handleDurumChange = (next) => {
    if (next === "active") {
      handleSetActive();
    } else {
      handleStatusChange(next);
    }
  };

  const [pdfOpen, setPdfOpen] = useState(false);

  // Optimistic gösterilecek mevcut durum değeri.
  const currentStatusValue =
    pendingStatus || (record?.isActive ? "active" : record?.status || "active");

  // ── Render ──

  // Bölüm başlığı (ortak bileşen); "Ekle" yalnız düzenleme yetkisinde
  const sectionHeader = (title, count, addLabel, onAdd, addDisabled = lockedNoDx) => (
    <SectionHeader
      title={title}
      count={count}
      addLabel={addLabel}
      onAdd={canEdit ? onAdd : undefined}
      disabled={addDisabled}
      palette={palette}
    />
  );

  // Bölüm içi boş durum; ipucu yalnız ekleme mümkünken
  const sectionEmpty = (icon, title, hint, ignoreLock = false) => (
    <SectionEmpty
      icon={icon}
      title={title}
      hint={canEdit && (ignoreLock || !lockedNoDx) ? hint : undefined}
    />
  );

  const FOOD_PICKERS = {
    allowed: {
      field: "allowedFoods",
      title: "Serbest Besin Ekle",
      subtitle: `${terms.patientGen} tüketebileceği besinleri seçin`,
      accent: COLORS.successDark,
    },
    forbidden: {
      field: "forbiddenFoods",
      title: "Yasaklı Besin Ekle",
      subtitle: `${terms.patientGen} kaçınması gereken besinleri seçin`,
      accent: COLORS.errorDark,
    },
  };
  const foodPicker = FOOD_PICKERS[picker];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <EditorHeader
        record={record}
        newLabel="Yeni tedavi kaydı oluşturuluyor"
        editLabel="Tedavi kaydı düzenleniyor"
        saveStatus={saveStatus}
        palette={palette}
        actions={
          <>
            {/* PDF çıktısı — salt-okunur kullanıcı da alabilmeli */}
            {!isNew && (
              <PdfButton
                title="Tedavi planı çıktısı (PDF)"
                onClick={() => setPdfOpen(true)}
                palette={palette}
              />
            )}
        {/* Durum — üstte, modern renkli seçici (ayrı buton yok) */}
        {!isNew && canEdit && (
          <Box sx={{ flexShrink: 0 }}>
            <FormSelect
              value={currentStatusValue}
              onChange={(val) => handleDurumChange(val)}
              disabled={statusChanging}
              size="small"
              fullWidth={false}
              placeholder=""
              SelectProps={{
                IconComponent: ArrowDownIcon,
                renderValue: (val) => {
                  const opt = STATUS_OPTIONS.find((o) => o.value === val);
                  const c = statusColor(val, palette);
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {statusChanging ? (
                        <CircularProgress size={12} thickness={6} sx={{ color: c }} />
                      ) : (
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "50%",
                            bgcolor: c,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        sx={{ fontSize: "0.85rem", fontWeight: 700, color: c }}
                      >
                        {opt?.label || "Durum"}
                      </Typography>
                    </Box>
                  );
                },
                MenuProps: {
                  PaperProps: {
                    sx: { borderRadius: 2, mt: 0.5, boxShadow: 3 },
                  },
                },
              }}
              sx={{
                minWidth: 168,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  bgcolor: COLORS.white,
                  "& fieldset": {
                    borderColor: alpha(statusColor(currentStatusValue, palette), 0.33),
                  },
                  "&:hover fieldset": {
                    borderColor: statusColor(currentStatusValue, palette),
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: statusColor(currentStatusValue, palette),
                    borderWidth: "1.5px",
                  },
                },
                "& .MuiSelect-select": {
                  py: 0.85,
                  pl: 1.75,
                  pr: 4,
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiSelect-icon": {
                  color: statusColor(currentStatusValue, palette),
                },
              }}
            >
              {STATUS_OPTIONS.map((o) => {
                const c = statusColor(o.value, palette);
                return (
                  <MenuItem
                    key={o.value}
                    value={o.value}
                    sx={{ fontSize: "0.85rem", fontWeight: 600, py: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          bgcolor: c,
                          flexShrink: 0,
                        }}
                      />
                      {o.label}
                    </Box>
                  </MenuItem>
                );
              })}
            </FormSelect>
          </Box>
        )}
          </>
        }
      />

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          position: "relative",
        }}
      >
        {/* Durum değişimi sürerken form skeleton'a döner ve tıklanamaz */}
        {statusChanging && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 6,
              bgcolor: "rgba(255,255,255,0.85)",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              px: 2,
              py: 2,
              overflow: "hidden",
            }}
          >
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <Box
                key={i}
                sx={{ display: "flex", gap: 2, alignItems: "center" }}
              >
                <Skeleton variant="text" width={110} height={22} />
                <Skeleton
                  variant="rounded"
                  height={30}
                  sx={{ flex: 1, borderRadius: 1 }}
                />
              </Box>
            ))}
          </Box>
        )}


        {/* Tanı zorunlu uyarısı */}
        {canEdit && !hasDiagnosis && (
          <Box
            role="alert"
            sx={{
              mx: { xs: 2, md: 3 },
              mr: { md: 4 },
              mt: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: COLORS.warningLight,
              border: `1px solid ${alpha(COLORS.warning, 0.4)}`,
            }}
          >
            <ReportProblemIcon sx={{ fontSize: 18, color: COLORS.warning }} />
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: COLORS.warningDarker,
              }}
            >
              Tanı zorunludur. Diğer alanları doldurabilmek için en az bir tanı
              ekleyin.
            </Typography>
          </Box>
        )}

        {/* Tanılar (zorunlu) — Özel Notlar bu bölümde */}
        <Box sx={SECTION_SX}>
          {sectionHeader(
            "Tanılar *",
            fieldValues.diagnoses.length,
            "Tanı Ekle",
            () => openPicker("diagnosis"),
            isDisabled
          )}
          <Box sx={listAreaSx(DIAGNOSIS_LIST_HEIGHT, fieldValues.diagnoses.length === 0)}>
          {fieldValues.diagnoses.length === 0 ? (
            sectionEmpty(
              <MedicalServicesIcon />,
              "Henüz tanı eklenmedi",
              canEdit ? "Tanı Ekle ile ICD listesinden seçin." : undefined,
              true
            )
          ) : (
            <Box>
              {fieldValues.diagnoses.map((d, idx) => {
                const code = d.diagnosisCode || d.code || "";
                const name = d.diagnosisName || d.name || "Tanı";
                return (
                  <Box key={`${getDiagnosisId(d)}-${idx}`} sx={ITEM_ROW_SX}>
                    <Typography
                      component="span"
                      sx={codeBadgeSx(
                        code ? palette.primary : COLORS.slate300,
                        alpha(palette.primary, 0.08)
                      )}
                    >
                      {code || "—"}
                    </Typography>
                    <Typography sx={{ ...ITEM_NAME_SX, flex: 1 }} noWrap title={name}>
                      {name}
                    </Typography>
                    {canEdit && (
                      <IconButton
                        size="small"
                        aria-label={`${name} kaldır`}
                        onClick={() => removeDiagnosis(idx)}
                        disabled={isDisabled}
                        sx={{
                          color: COLORS.slate400,
                          "&:hover": { color: COLORS.errorDark },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
          </Box>
          <FieldRow label="Özel Notlar">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Tedaviyle ilgili notlar"
              value={fieldValues.specialNotes || ""}
              onChange={(e) => setField("specialNotes", e.target.value)}
              disabled={lockedNoDx}
            />
          </FieldRow>
        </Box>

        {/* Şikayetler */}
        <Box sx={SECTION_SX}>
          {sectionHeader(
            "Şikayetler *",
            fieldValues.complaints.length,
            "Şikayet Ekle",
            () => openPicker("complaint")
          )}
          <Box sx={listAreaSx(COMPLAINT_LIST_HEIGHT, fieldValues.complaints.length === 0)}>
          {fieldValues.complaints.length === 0 ? (
            sectionEmpty(
              <ReportProblemIcon />,
              "Henüz şikayet eklenmedi",
              `${terms.patientGen} şikayetlerini Şikayet Ekle ile listeden seçin.`
            )
          ) : (
            <Box>
              {fieldValues.complaints.map((c, idx) => (
                <Box key={`${c}-${idx}`} sx={ITEM_ROW_SX}>
                  <ReportProblemIcon
                    sx={{ color: COLORS.warning, fontSize: 18, flexShrink: 0 }}
                  />
                  <Typography sx={{ ...ITEM_NAME_SX, flex: 1 }} noWrap title={c}>
                    {c}
                  </Typography>
                  {canEdit && (
                    <IconButton
                      size="small"
                      aria-label={`${c} kaldır`}
                      onClick={() => removeComplaint(idx)}
                      sx={{
                        color: COLORS.slate400,
                        "&:hover": { color: COLORS.errorDark },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}
          </Box>
        </Box>

        {/* Grup: Reçete — İlaçlar + Reçete Notu (reçete modülü kapalıysa yok) */}
        {prescriptionModule && (<>
        <Box sx={GROUP_BOX_SX}>
          <Typography
            sx={{ ...GROUP_TITLE_SX, color: palette?.primary || COLORS.slate700 }}
          >
            Reçete
          </Typography>

        {/* İlaçlar */}
        <Box sx={SECTION_SX}>
          {sectionHeader(
            "İlaçlar",
            fieldValues.medications.length,
            "İlaç Ekle",
            () => openPicker("medication")
          )}
          <Box sx={listAreaSx(MED_LIST_HEIGHT, fieldValues.medications.length === 0)}>
          {fieldValues.medications.length === 0 ? (
            sectionEmpty(
              <LocalPharmacyIcon />,
              "Henüz ilaç eklenmedi",
              "Reçeteye ilaç eklemek için İlaç Ekle'yi kullanın."
            )
          ) : (
            <Box>
              {fieldValues.medications.map((m, idx) => (
                <Box
                  key={`${getRefId(m.medicine) || idx}-${idx}`}
                  sx={ITEM_ROW_SX}
                >
                  <LocalPharmacyIcon
                    sx={{ color: palette.primary, fontSize: 18, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={ITEM_NAME_SX}
                      noWrap
                    >
                      {m.name ||
                        (typeof m.medicine === "object"
                          ? m.medicine?.name
                          : null) ||
                        "İlaç"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.75rem", color: COLORS.slate500 }}
                    >
                      {[
                        m.dose1 != null &&
                          `Doz: ${m.dose1}${m.dose2 != null ? `×${m.dose2}` : ""}`,
                        m.usagePeriod?.value &&
                          `${m.usagePeriod.value} ${m.usagePeriod.unit}`,
                        m.box != null && `Kutu: ${m.box}`,
                        m.quantity != null && `Adet: ${m.quantity}`,
                        m.usageMethod,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                  {canEdit && (
                    <IconButton
                      size="small"
                      onClick={() => removeMedication(idx)}
                      sx={{
                        color: COLORS.slate400,
                        "&:hover": { color: COLORS.errorDark },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}
          </Box>
        </Box>

        {/* Reçete Notu */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>Reçete Notu</Typography>
          <Box>
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={3}
              placeholder="Reçete ile ilgili notlar"
              value={fieldValues.prescriptionNote || ""}
              onChange={(e) => setField("prescriptionNote", e.target.value)}
              disabled={lockedNoDx}
            />
          </Box>
        </Box>
        </Box>
        </>)}
        {/* /Grup: Reçete */}

        {/* Grup: Diyet — Serbest/Yasaklı Besinler + Diyet Notu (diyet modülü kapalıysa yok) */}
        {dietModule && (<>
        <Box sx={GROUP_BOX_SX}>
          <Typography
            sx={{ ...GROUP_TITLE_SX, color: palette?.primary || COLORS.slate700 }}
          >
            Diyet
          </Typography>

        {/* Diyet planı artık ayrı kayıt (Diyet sekmesi / Diyetler sayfası). Tedavide
            yalnız diyet talebi + diyetisyene not; besin listeleri burada düzenlenmez. */}
        <Box
          sx={{
            ...SECTION_SX,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
            {fieldValues.dietRequested
              ? "Bu tedavi için diyet planı talebi açık."
              : record?.diet
                ? "Bu tedaviye bağlı bir diyet planı var (Diyet sekmesi)."
                : "Diyet planı gerekiyorsa talep açın; diyetisyen planı yazar."}
          </Typography>
          {!lockedNoDx && (
            <ActionButton
              variant={fieldValues.dietRequested ? "delete" : "confirm"}
              palette={palette}
              compact
              onClick={() => setField("dietRequested", !fieldValues.dietRequested)}
              label={fieldValues.dietRequested ? "Diyet Talebini Kaldır" : "Diyet Talebi Aç"}
            />
          )}
        </Box>

        {/* Diyet Notu */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>Diyet Notu (diyetisyene)</Typography>
          <Box>
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={3}
              placeholder="Diyet ile ilgili notlar"
              value={fieldValues.dietNote || ""}
              onChange={(e) => setField("dietNote", e.target.value)}
              disabled={lockedNoDx}
            />
          </Box>
        </Box>
        </Box>
        </>)}
        {/* /Grup: Diyet */}

        {/* Gelecek Kontrol */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>Gelecek Kontrol</Typography>
          <FieldRow label="Tarih">
            <FormDatePicker
              palette={palette}
              value={fieldValues.nextControlDate || ""}
              onChange={(ymd) => setField("nextControlDate", ymd)}
              disabled={lockedNoDx}
              sx={{ maxWidth: 220 }}
            />
          </FieldRow>
          <FieldRow label="Not">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Kontrol notu"
              value={fieldValues.nextControlNote || ""}
              onChange={(e) => setField("nextControlNote", e.target.value)}
              disabled={lockedNoDx}
            />
          </FieldRow>
        </Box>
      </Box>

      {/* PDF önizleme — kaydedilmiş kayıt DEĞİL, ekrandaki güncel alanlar
          basılır; autosave gecikmesi yüzünden son düzenleme çıktıda eksik
          kalmasın. */}
      <TagPickerDrawer
        open={pickerOpen && picker === "diagnosis"}
        onClose={closePicker}
        icon={<MedicalServicesIcon />}
        title="Tanı Ekle"
        subtitle="ICD listesinden tanı seçin"
        fetchPage={fetchDiagnoses}
        existing={(fieldValues.diagnoses || []).map((d) => String(getDiagnosisId(d)))}
        searchPlaceholder="Tanı adı veya kodu ara"
        noun="tanı"
        onSubmit={(_, options) => {
          addDiagnoses(options);
          closePicker();
        }}
      />

      <TagPickerDrawer
        open={pickerOpen && picker === "complaint"}
        onClose={closePicker}
        icon={<ReportProblemIcon />}
        accent={COLORS.warning}
        title="Şikayet Ekle"
        subtitle={`${terms.patientGen} şikayetlerini seçin`}
        fetchPage={fetchComplaints}
        existing={fieldValues.complaints || []}
        searchPlaceholder="Şikayet ara"
        noun="şikayet"
        onSubmit={(values) => {
          addComplaints(values);
          closePicker();
        }}
      />

      <TagPickerDrawer
        open={pickerOpen && Boolean(foodPicker)}
        onClose={closePicker}
        icon={<RestaurantIcon />}
        accent={foodPicker?.accent}
        title={foodPicker?.title}
        subtitle={foodPicker?.subtitle}
        fetchPage={fetchFoods}
        existing={(foodPicker ? fieldValues[foodPicker.field] || [] : []).map((f) => f.name)}
        searchPlaceholder="Besin ara"
        noun="besin"
        onSubmit={(_, options) => {
          if (foodPicker) addFoods(foodPicker.field, options.map((o) => o.raw));
          closePicker();
        }}
      />

      <MedicationPickerDrawer
        open={pickerOpen && picker === "medication"}
        onClose={closePicker}
        fetchPage={fetchMedicines}
        existingIds={
          new Set((fieldValues.medications || []).map((m) => String(getRefId(m.medicine))))
        }
        form={medicineForm}
        onSelectMedicine={selectMedicine}
        onFormChange={(patch) => setMedicineForm((p) => ({ ...p, ...patch }))}
        onSubmit={addMedication}
        canSubmit={Boolean(canAddMedication)}
      />

      {pdfOpen && (
        <Suspense fallback={null}>
          <TreatmentPlanPreview
            open={pdfOpen}
            onClose={() => setPdfOpen(false)}
            patient={patient}
            treatment={{
              ...(record || {}),
              ...fieldValues,
              status: currentStatusValue,
            }}
          />
        </Suspense>
      )}
    </Box>
  );
}
